export const SUPPORTED_CURRENCIES = [
  'USD',
  'EUR',
  'ARS',
  'BRL',
  'CLP',
  'COP',
  'MXN',
  'PEN',
  'PYG',
  'UYU',
] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

const ZERO_DECIMAL_CURRENCIES = new Set<SupportedCurrency>(['CLP', 'PYG']);

export function normalizeCurrency(
  currency: string | null | undefined,
  fallback: SupportedCurrency = 'USD',
): SupportedCurrency {
  const upper = (currency ?? '').trim().toUpperCase();
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(upper)
    ? (upper as SupportedCurrency)
    : fallback;
}

export function isZeroDecimalCurrency(
  currency: string | null | undefined,
): boolean {
  return ZERO_DECIMAL_CURRENCIES.has(normalizeCurrency(currency));
}

export function toStripeAmount(
  majorAmount: number,
  currency: string | null | undefined,
): number {
  const amount = Number.isFinite(majorAmount) ? majorAmount : 0;
  return isZeroDecimalCurrency(currency)
    ? Math.round(amount)
    : Math.round(amount * 100);
}

export function formatMinorAmount(
  minorAmount: number,
  currency: string | null | undefined,
  locale = 'es-ES',
): string {
  const normalized = normalizeCurrency(currency);
  const amount = Number.isFinite(minorAmount) ? minorAmount : 0;
  const value = isZeroDecimalCurrency(normalized) ? amount : amount / 100;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: normalized,
    minimumFractionDigits: isZeroDecimalCurrency(normalized) ? 0 : 2,
  }).format(value);
}

export function formatMajorAmount(
  majorAmount: number,
  currency: string | null | undefined,
  locale = 'es-ES',
): string {
  const normalized = normalizeCurrency(currency);
  const amount = Number.isFinite(majorAmount) ? majorAmount : 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: normalized,
    minimumFractionDigits: isZeroDecimalCurrency(normalized) ? 0 : 2,
  }).format(amount);
}
